import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  vscDarkPlus,
  vs,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  Box,
  Code,
  Link,
  OrderedList,
  UnorderedList,
  ListItem,
  Text,
  Heading,
  Divider,
  useColorMode,
  Button,
  HStack,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import './MessageContent.css';

interface MessageContentProps {
  content: string;
  searchQuery?: string;
}

interface ActionButton {
  label: string;
  action: string;
  url?: string;
}

const MessageContent: React.FC<MessageContentProps> = ({
  content,
  searchQuery,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const navigate = useNavigate();

  // Function to wrap search terms with highlight markers
  const wrapSearchHighlights = (text: string, query: string): string => {
    if (!query || !text) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let result = '';
    let lastIndex = 0;
    let searchIndex = lowerText.indexOf(lowerQuery, lastIndex);

    while (searchIndex !== -1) {
      result += text.substring(lastIndex, searchIndex);

      // Add highlighted match wrapped in a special marker
      result += `<span class="search-highlight">${text.substring(searchIndex, searchIndex + query.length)}</span>`;

      lastIndex = searchIndex + query.length;
      searchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    }
    result += text.substring(lastIndex);

    return result;
  };

  // Parse action buttons from content
  // Format: [Button:Label:action:url] or [Button:Label:navigate:/path]
  const parseActionButtons = (
    text: string
  ): { text: string; buttons: ActionButton[] } => {
    const buttonRegex = /\[Button:(.*?):(.*?)(?::(.*?))?\]/g;
    const buttons: ActionButton[] = [];
    let match;

    while ((match = buttonRegex.exec(text)) !== null) {
      buttons.push({
        label: match[1],
        action: match[2],
        url: match[3],
      });
    }

    const cleanText = text.replace(buttonRegex, '').trim();
    return { text: cleanText, buttons };
  };

  const { text: cleanContent, buttons } = parseActionButtons(content);

  // Apply highlighting if search query exists
  const contentToRender = searchQuery
    ? wrapSearchHighlights(cleanContent, searchQuery)
    : cleanContent;

  const handleButtonClick = (button: ActionButton) => {
    if (button.action === 'navigate' && button.url) {
      navigate(button.url);
    } else if (button.action === 'link' && button.url) {
      window.open(button.url, '_blank');
    } else if (button.action === 'scroll') {
      const element = document.getElementById(button.url || '');
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Custom renderers for markdown elements
  const components = {
    code({ inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';

      return !inline && language ? (
        <Box my={2} borderRadius='md' overflow='hidden'>
          <SyntaxHighlighter
            style={isDark ? vscDarkPlus : vs}
            language={language}
            PreTag='div'
            customStyle={{
              margin: 0,
              borderRadius: '6px',
              fontSize: '0.875rem',
            }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </Box>
      ) : (
        <Code
          colorScheme='gray'
          fontSize='0.875em'
          px={1.5}
          py={0.5}
          borderRadius='md'
          {...props}
        >
          {children}
        </Code>
      );
    },

    // Links
    a({ children, href, ...props }: any) {
      const isExternal = href?.startsWith('http');
      return (
        <Link
          href={href}
          isExternal={isExternal}
          color='blue.400'
          textDecoration='underline'
          _hover={{ color: 'blue.300' }}
          {...props}
        >
          {children}
          {isExternal && <ExternalLinkIcon mx='2px' mb='2px' />}
        </Link>
      );
    },

    // Paragraphs
    p({ children, ...props }: any) {
      return (
        <Text mb={2} lineHeight='1.6' {...props}>
          {children}
        </Text>
      );
    },

    // Headings
    h1({ children, ...props }: any) {
      return (
        <Heading as='h1' size='lg' mt={4} mb={2} {...props}>
          {children}
        </Heading>
      );
    },
    h2({ children, ...props }: any) {
      return (
        <Heading as='h2' size='md' mt={3} mb={2} {...props}>
          {children}
        </Heading>
      );
    },
    h3({ children, ...props }: any) {
      return (
        <Heading as='h3' size='sm' mt={2} mb={1} {...props}>
          {children}
        </Heading>
      );
    },

    // Lists
    ul({ children, ...props }: any) {
      return (
        <UnorderedList spacing={1} pl={4} my={2} {...props}>
          {children}
        </UnorderedList>
      );
    },
    ol({ children, ...props }: any) {
      return (
        <OrderedList spacing={1} pl={4} my={2} {...props}>
          {children}
        </OrderedList>
      );
    },
    li({ children, ...props }: any) {
      return <ListItem {...props}>{children}</ListItem>;
    },

    // Horizontal rule
    hr({ ...props }: any) {
      return <Divider my={3} {...props} />;
    },

    // Blockquote
    blockquote({ children, ...props }: any) {
      return (
        <Box
          borderLeft='4px'
          borderColor='gray.500'
          pl={4}
          py={1}
          my={2}
          fontStyle='italic'
          opacity={0.9}
          {...props}
        >
          {children}
        </Box>
      );
    },

    // Strong/Bold
    strong({ children, ...props }: any) {
      return (
        <Text as='strong' fontWeight='bold' {...props}>
          {children}
        </Text>
      );
    },

    // Emphasis/Italic
    em({ children, ...props }: any) {
      return (
        <Text as='em' fontStyle='italic' {...props}>
          {children}
        </Text>
      );
    },

    // Tables (GitHub Flavored Markdown)
    table({ children, ...props }: any) {
      return (
        <Box overflowX='auto' my={2}>
          <Box
            as='table'
            width='100%'
            borderWidth='1px'
            borderRadius='md'
            {...props}
          >
            {children}
          </Box>
        </Box>
      );
    },
    thead({ children, ...props }: any) {
      return (
        <Box as='thead' bg={isDark ? 'gray.700' : 'gray.100'} {...props}>
          {children}
        </Box>
      );
    },
    tbody({ children, ...props }: any) {
      return (
        <Box as='tbody' {...props}>
          {children}
        </Box>
      );
    },
    tr({ children, ...props }: any) {
      return (
        <Box as='tr' borderBottomWidth='1px' {...props}>
          {children}
        </Box>
      );
    },
    td({ children, ...props }: any) {
      return (
        <Box as='td' px={3} py={2} {...props}>
          {children}
        </Box>
      );
    },
    th({ children, ...props }: any) {
      return (
        <Box
          as='th'
          px={3}
          py={2}
          textAlign='left'
          fontWeight='bold'
          {...props}
        >
          {children}
        </Box>
      );
    },
  };

  return (
    <Box className='message-content' fontSize='sm'>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {contentToRender}
      </ReactMarkdown>

      {/* Render action buttons if any */}
      {buttons.length > 0 && (
        <HStack spacing={2} mt={3} flexWrap='wrap'>
          {buttons.map((button, index) => (
            <Button
              key={index}
              size='sm'
              colorScheme='blue'
              variant='solid'
              onClick={() => handleButtonClick(button)}
              borderRadius='full'
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
              transition='all 0.2s'
            >
              {button.label}
            </Button>
          ))}
        </HStack>
      )}
    </Box>
  );
};

export default MessageContent;
